import { useEffect, useMemo, useRef, useState } from 'react'
import { useCollapse } from 'react-collapsed'

import type { ConversationTimelineItem } from './types'
import { checkIsReplied } from './utils'

export const useConversationExpanded = (
  status?: string,
  isMobile?: boolean
) => {
  const [isExpanded, setIsExpanded] = useState(
    status === 'requestChange' && isMobile
  )

  const { getCollapseProps, getToggleProps } = useCollapse({
    defaultExpanded: false,
    isExpanded,
  })

  return {
    isExpanded,
    setIsExpanded,
    getCollapseProps,
    getToggleProps,
  }
}

export const useConversationReplied = (
  isRepliedDesktop?: boolean,
  status?: string,
  messages?: ConversationTimelineItem[]
): boolean => {
  return useMemo(() => {
    if (isRepliedDesktop) {
      return Boolean(isRepliedDesktop)
    }

    return checkIsReplied(status, messages)
  }, [isRepliedDesktop, status, messages])
}

export const useConversationLoader = (
  isExpanded: boolean,
  isLoading: boolean,
  reviewKey?: string,
  postId?: string,
  fetchComment?: (reviewKey: string, postId: string) => void
) => {
  const lastFetchedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!reviewKey || !postId || !fetchComment) {
      return
    }

    if (!isExpanded) {
      lastFetchedKeyRef.current = null
      return
    }

    if (isLoading) {
      return
    }

    const currentKey = `${reviewKey}-${postId}`

    if (lastFetchedKeyRef.current === currentKey) {
      return
    }

    lastFetchedKeyRef.current = currentKey
    fetchComment(reviewKey, postId)
  }, [isExpanded, isLoading, reviewKey, postId, fetchComment])
}
